import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { oktaConfig } from "../config/auth";
import { isAuthenticated, verifyOktaWithBackend } from "../services/authService";
import {
  setAuthSession,
  clearAuthSession,
  getAccessToken,
  getIdToken,
  getTokenExpiry,
} from "../services/authSessionService";
import { runAuthorizationCodeExchangeOnce } from "../utils/authorizationCodeSingleFlight";
import { mapSignInFlowError } from "../utils/oktaSignInErrors";
import { randomString, sha256Base64Url } from "../utils/pkce";
import { logHostDocumentFileName } from "../services/hostDocumentInfoService";

export const LoginPage = () => {
  const navigate = useNavigate();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [sessionRestore, setSessionRestore] = useState<"idle" | "checking">(() =>
    isAuthenticated() ? "checking" : "idle",
  );
  const hasError = errorMessage.length > 0;
  const buttonLabel = isRedirecting
    ? "Redirecting to Okta..."
    : "Sign in with Okta";
  const helperText = isRedirecting
    ? "A secure sign-in window is opening. Complete authentication there and return here."
    : "We use your Okta identity for secure access.";

  const completeAuthorizationCodeFlow = useCallback(
    async (params: {
      code?: string;
      state?: string;
      error?: string;
      error_description?: string;
    }) => {
      const code = params.code ?? "";
      const state = params.state ?? "";
      const error = params.error ?? "";
      const errorDesc = params.error_description ?? "";

      if (error) {
        clearAuthSession();
        throw new Error(
          `Okta error: ${error}${errorDesc ? ` - ${errorDesc}` : ""}`,
        );
      }
      if (!code) {
        clearAuthSession();
        throw new Error("No authorization code received from Okta.");
      }

      const expectedState = sessionStorage.getItem("pkce_state");
      if (!expectedState || state !== expectedState) {
        clearAuthSession();
        throw new Error(
          "State parameter mismatch. Please try logging in again.",
        );
      }

      const codeVerifier = sessionStorage.getItem("pkce_code_verifier");
      if (!codeVerifier) {
        clearAuthSession();
        throw new Error("Missing PKCE verifier. Please try logging in again.");
      }

      const { issuer, clientId, redirectUri } = oktaConfig;

      await runAuthorizationCodeExchangeOnce(code, async () => {
        const body = new URLSearchParams({
          grant_type: "authorization_code",
          code,
          redirect_uri: redirectUri,
          client_id: clientId,
          code_verifier: codeVerifier,
        });

        const res = await fetch(`${issuer}/v1/token`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: body.toString(),
        });
        if (!res.ok) {
          const errBody = await res.text();
          throw new Error(`Token exchange failed (${res.status}): ${errBody}`);
        }

        const tokens = await res.json();
        const accessToken = String(tokens.access_token ?? "");
        const idToken = String(tokens.id_token ?? "");
        if (!accessToken && !idToken) {
          throw new Error("Token response had no access_token or id_token.");
        }

        const expiresAt =
          tokens.expires_in != null
            ? Date.now() + Number(tokens.expires_in) * 1000
            : null;
        const verified = await verifyOktaWithBackend(accessToken, idToken);

        sessionStorage.removeItem("pkce_code_verifier");
        sessionStorage.removeItem("pkce_state");

        setAuthSession({
          accessToken,
          idToken,
          expiresAt,
          user: verified.user,
          effectiveAccess: verified.effective_access,
        });
        void logHostDocumentFileName("after Okta sign-in");
        navigate("/", { replace: true });
      });
    },
    [navigate],
  );

  useEffect(() => {
    if (!isAuthenticated()) {
      setSessionRestore("idle");
      return;
    }
    let cancelled = false;
    void (async () => {
      setSessionRestore("checking");
      try {
        const accessToken = getAccessToken() ?? "";
        const idToken = getIdToken() ?? "";
        const verified = await verifyOktaWithBackend(accessToken, idToken);
        if (cancelled) return;
        if (!verified.valid) {
          clearAuthSession();
          setSessionRestore("idle");
          return;
        }
        setAuthSession({
          accessToken,
          idToken,
          expiresAt: getTokenExpiry(),
          user: verified.user,
          effectiveAccess: verified.effective_access,
        });
        navigate("/", { replace: true });
      } catch {
        if (cancelled) return;
        clearAuthSession();
        setSessionRestore("idle");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleLogin = useCallback(async () => {
    setErrorMessage("");
    const { issuer, clientId, redirectUri, scopes } = oktaConfig;
    if (!issuer || !clientId) {
      setErrorMessage(
        "Okta is not configured. Set VITE_OKTA_ISSUER and VITE_OKTA_CLIENT_ID in addin/.env",
      );
      return;
    }

    setIsRedirecting(true);
    const codeVerifier = randomString(64);
    const codeChallenge = await sha256Base64Url(codeVerifier);
    const state = randomString(32);

    sessionStorage.setItem("pkce_code_verifier", codeVerifier);
    sessionStorage.setItem("pkce_state", state);

    const params = new URLSearchParams({
      client_id: clientId,
      response_type: "code",
      redirect_uri: redirectUri,
      scope: scopes,
      state,
      code_challenge: codeChallenge,
      code_challenge_method: "S256",
    });

    const authorizeUrl = `${issuer}/v1/authorize?${params.toString()}`;
    const dialogStartUrl = `${window.location.origin}/okta-dialog-start?target=${encodeURIComponent(authorizeUrl)}`;

    const ui =
      typeof Office !== "undefined" && Office?.context?.ui
        ? Office.context.ui
        : null;
    if (ui?.displayDialogAsync) {
      ui.displayDialogAsync(
        dialogStartUrl,
        {
          height: 70,
          width: 45,
          promptBeforeOpen: false,
          displayInIframe: false,
        },
        (asyncResult) => {
          if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
            const maybeError = asyncResult.error as
              | { code?: number; message?: string }
              | undefined;
            const suffix = maybeError?.code
              ? ` (code: ${maybeError.code}${maybeError.message ? `, ${maybeError.message}` : ""})`
              : "";
            setErrorMessage(
              `Unable to open sign-in dialog. Please try again${suffix}.`,
            );
            setIsRedirecting(false);
            return;
          }
          const dialog = asyncResult.value;
          dialog.addEventHandler(
            Office.EventType.DialogMessageReceived,
            (arg: { message: string; origin?: string } | { error: number }) => {
              if (!("message" in arg)) {
                setErrorMessage(
                  "Sign-in dialog returned an unexpected response.",
                );
                dialog.close();
                setIsRedirecting(false);
                return;
              }
              void (async () => {
                try {
                  const payload = JSON.parse(arg.message) as {
                    code?: string;
                    state?: string;
                    error?: string;
                    error_description?: string;
                  };
                  await completeAuthorizationCodeFlow(payload);
                } catch (e) {
                  clearAuthSession();
                  setErrorMessage(mapSignInFlowError(e));
                } finally {
                  dialog.close();
                  setIsRedirecting(false);
                }
              })();
            },
          );
          dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
            // Dialog closed or navigation failed — allow another Sign in attempt (see Office dialog lifecycle).
            setIsRedirecting(false);
          });
        },
      );
      return;
    }

    window.location.href = authorizeUrl;
  }, [completeAuthorizationCodeFlow]);

  if (sessionRestore === "checking") {
    return (
      <div className="flex min-h-[calc(100vh-4rem)] h-full items-center justify-center px-4">
        <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card px-8 py-10 shadow-sm">
          <span
            className="h-8 w-8 animate-spin rounded-full border-2 border-primary/30 border-t-primary"
            aria-hidden="true"
          />
          <p className="text-sm text-muted-foreground">Checking your session…</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative flex min-h-[calc(100vh-4rem)] h-full items-center justify-center overflow-hidden px-2 py-6"
      style={{
        backgroundImage:
          "linear-gradient(108deg,#06c,rgba(0,51,102,0)),linear-gradient(267deg,rgba(9,9,121,0) 100%,#06c),url('https://www.tmhcc.com/assets/images/right-wave-bg.png')",
        backgroundRepeat: "no-repeat, no-repeat, no-repeat",
        backgroundPosition: "center, center, right center",
        backgroundSize: "auto, auto, 125% 50%",
      }}
    >
      <div className="pointer-events-none absolute -left-16 -top-20 h-44 w-44 rounded-full bg-primary/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -right-14 h-44 w-44 rounded-full bg-indigo-500/20 blur-3xl" />

      <section className="relative w-full max-w-sm overflow-hidden rounded-2xl border border-border/70 bg-white shadow-xl backdrop-blur-sm">
        <div className="border-b border-border/60 bg-gradient-to-r from-primary/15 via-indigo-500/10 to-transparent px-6 py-4">
          <div className="flex w-full gap-4 mb-3 items-center">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-600 to-purple-700 text-sm font-bold text-white shadow-sm">
              IQ
            </div>
            <h1 className="text-xl font-semibold tracking-tight text-foreground">
              Welcome to IntelliDocSuite
            </h1>
          </div>

          <p className="mt-1 text-sm text-muted-foreground">
            Sign in to review contracts, templates, and recommendations.
          </p>
        </div>

        <div className="space-y-4 px-6 py-5">
          <button
            type="button"
            onClick={() => void handleLogin()}
            disabled={isRedirecting}
            aria-busy={isRedirecting}
            className="group inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:translate-y-0 disabled:opacity-75 disabled:shadow-none"
          >
            {isRedirecting && (
              <span
                className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground/40 border-t-primary-foreground"
                aria-hidden="true"
              />
            )}
            <span>{buttonLabel}</span>
          </button>

          <p className="text-center rounded-md border border-border/70 bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            {helperText}
          </p>

          {hasError && (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {errorMessage}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
